
import { GoogleGenAI, Type } from "@google/genai";
import { AiAnalysisKpis, KpiDefinition } from "../types";

const basicKpiSchema = {
    type: Type.OBJECT,
    properties: {
        analysis: { type: Type.STRING, description: "El análisis cualitativo. Debe ser conciso y no exceder dos frases." },
        score: { type: Type.NUMBER, description: "Una puntuación numérica del 1 al 10 evaluando el desempeño en este KPI." }
    },
    required: ["analysis", "score"]
};

export const defaultKpis: KpiDefinition[] = [
    { id: 'saludo_bienvenida', title: 'Saludo y Bienvenida', description: 'Análisis del saludo y la bienvenida ofrecida por el empleado al cliente.' },
    { id: 'conocimiento_servicio', title: 'Conocimiento del Servicio', description: 'Evaluación del conocimiento y manejo del servicio/producto demostrado por el empleado.' },
    { id: 'actitud_emociones', title: 'Actitud y Emociones', description: 'Análisis de la actitud y las emociones transmitidas por el empleado durante la interacción.' },
    { id: 'habilidades_cierre', title: 'Habilidades de Cierre', description: 'Evaluación de las habilidades y técnicas de cierre de venta o finalización de la interacción.' },
];

const buildDynamicSchema = (kpis: KpiDefinition[]) => {
    const kpiProperties: Record<string, any> = {
        resumen: { type: Type.STRING, description: "Un resumen muy conciso (1-2 frases) de la interacción grabada." },
        areas_mejora_capacitacion: { type: Type.STRING, description: "Identificación de áreas de mejora y sugerencias para capacitación. Sé breve y directo." },
    };
    
    const requiredFields = ["resumen", "areas_mejora_capacitacion"];

    kpis.forEach(kpi => {
        kpiProperties[kpi.id] = { 
            ...basicKpiSchema, 
            description: kpi.description 
        };
        requiredFields.push(kpi.id);
    });

    return {
        type: Type.OBJECT,
        properties: kpiProperties,
        required: requiredFields
    };
};

const buildSystemInstruction = (kpis: KpiDefinition[]) => {
    let instruction = `
Eres un analista experto de Mystery Shopper para la consultora MysteryShopperIA.
Tu tarea es analizar una secuencia de fotogramas de un video de una interacción entre un empleado y un cliente.
Observa los gestos, el lenguaje corporal y el contexto visual.
Debes ser objetivo, profesional y proveer insights valiosos y concisos.

Evalúa los siguientes KPIs con una puntuación del 1 al 10 (1 pobre, 10 excelente) y un breve análisis:
`;

    kpis.forEach(kpi => {
        instruction += `- **${kpi.title}**: ${kpi.description}\n`;
    });

    instruction += `
Genera también un "resumen" general y "areas_mejora_capacitacion".
El análisis para cada KPI debe ser breve y no exceder las dos frases.
Para 'areas_mejora_capacitacion', proporciona sugerencias de texto muy directas y breves.
Genera un análisis en formato JSON estricto basado en el esquema proporcionado.
`;
    return instruction;
};

export const generateVideoAnalysis = async (
    videoFrames: string[], 
    customKpis?: KpiDefinition[]
): Promise<AiAnalysisKpis> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  
  try {
    const kpisToUse = (customKpis && customKpis.length > 0) ? customKpis : defaultKpis;
    const dynamicSchema = buildDynamicSchema(kpisToUse);
    const dynamicInstruction = buildSystemInstruction(kpisToUse);

    const textPart = { text: "Analiza la siguiente secuencia de fotogramas extraídos de un video de una interacción de servicio al cliente y genera el informe en JSON." };
    const imageParts = videoFrames.map(frameData => ({
        inlineData: {
            mimeType: 'image/jpeg',
            data: frameData,
        },
    }));

    const response = await ai.models.generateContent({
        model: "gemini-3-pro-preview",
        contents: { parts: [textPart, ...imageParts] },
        config: {
            systemInstruction: dynamicInstruction,
            responseMimeType: "application/json",
            responseSchema: dynamicSchema,
            temperature: 0.5,
        }
    });

    const jsonText = response.text?.trim();
    if (!jsonText) throw new Error("Empty response from AI");

    return JSON.parse(jsonText) as AiAnalysisKpis;

  } catch (error) {
    console.error("Error generating video analysis with Gemini:", error);
    throw new Error(`No se pudo generar el análisis de IA: ${(error as Error).message}`);
  }
};

export const generateAggregatedAnalysis = async (
  data: { title: string; kpis: AiAnalysisKpis }[]
): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  const prompt = `
  Actúa como un Consultor Senior de Calidad y Experiencia de Cliente (CX) en MysteryShopperIA.
  Tienes acceso a los resultados consolidados de múltiples evaluaciones de "Mystery Shopper" de una empresa.
  
  Tu tarea es realizar un **Análisis Ejecutivo Global** basado en los siguientes datos de evaluaciones.
  Identifica patrones sistémicos, fortalezas culturales, debilidades recurrentes y sugiere un plan de acción estratégico.
  
  DATOS DE LAS EVALUACIONES:
  ${JSON.stringify(data, null, 2)}

  INSTRUCCIONES DE FORMATO:
  Genera la respuesta en formato Markdown limpio y profesional. Estructura el reporte de la siguiente manera:

  # Informe Ejecutivo de Desempeño

  ## 1. Resumen Ejecutivo
  [Una visión holística y breve del estado actual de la atención al cliente basada en los videos analizados]

  ## 2. Fortalezas Principales (Top 3)
  * **[Fortaleza 1]**: [Explicación detallada]
  * **[Fortaleza 2]**: [Explicación detallada]
  * **[Fortaleza 3]**: [Explicación detallada]

  ## 3. Áreas de Oportunidad Críticas (Top 3)
  * **[Debilidad 1]**: [Explicación detallada y frecuencia observada]
  * **[Debilidad 2]**: [Explicación detallada y frecuencia observada]
  * **[Debilidad 3]**: [Explicación detallada y frecuencia observada]

  ## 4. Tendencias de KPIs
  [Un párrafo analizando la consistencia de los puntajes. ¿Son consistentes o varían mucho entre empleados?]

  ## 5. Plan de Acción Recomendado (Sugerencias de Capacitación)
  [Lista de 3 a 5 acciones concretas y accionables para mejorar los resultados a corto y mediano plazo]
  `;

  try {
      const response = await ai.models.generateContent({
        model: "gemini-3-pro-preview",
        contents: { parts: [{ text: prompt }] },
        config: {
            temperature: 0.7,
        }
      });
      return response.text || "No se pudo generar el análisis.";
  } catch (error) {
      console.error("Error generating aggregated analysis:", error);
      throw new Error("Error al conectar con la IA para el análisis general.");
  }
};